import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import React, { useState } from 'react';

import {
  drawerStyle,
  platinumPopover,
} from '../../common/CheckBoxGroup/CheckBoxInfoPopover.styled';
import { useLagoon } from '../../hooks/useLagoon';
import {
  Constants,
  FILTER_INFO_POPOVER_CONFIG,
  FilterCollectionModel,
} from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { phoneOnly } from '../ConstantsStyles';

interface CheckBoxPopoverProps {
  filterModel: FilterCollectionModel;
}

export const CheckBoxPopover = ({ filterModel }: CheckBoxPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();

  // Get configuration for the specific filter model
  const config = FILTER_INFO_POPOVER_CONFIG[filterModel];

  // If no configuration exists for this filter model, don't render anything
  if (!config) {
    return null;
  }

  const popoverContent = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING[
      config.contentKey as keyof typeof ConstantsLagoon.UI_MESSAGING
    ],
  });

  const popoverInfoText = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING[
      config.infoTitleKey as keyof typeof ConstantsLagoon.UI_MESSAGING
    ],
  });

  const popoverTitleData = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING[
      config.popoverTitle as keyof typeof ConstantsLagoon.UI_MESSAGING
    ],
  });

  const title: string = popoverTitleData?.title;
  const mobileScreen = useMediaQuery(phoneOnly);

  const content = () => (
    <Flex
      alignItems="center"
      data-auto-testid="filter-info-icon-button"
      data-testid="filter-info-icon-button"
      onClick={() => setIsOpen(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsOpen(true);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <IconMaterial icon="info" size={16} variant="outlined" />
      <Text
        css={{
          lineHeight: '18px',
          fontSize: '14px',
          color: '$interactive1 !important',
          paddingLeft: '8px',
          fontWeight: '$semibold',
        }}
      >
        {popoverInfoText?.message}
      </Text>
    </Flex>
  );

  return (
    <React.Fragment>
      {!mobileScreen ? (
        <Popover
          content={popoverContent?.message}
          css={platinumPopover}
          data-testid="filter-info-icon-popover"
          onOpenChange={() => {
            setIsOpen(!isOpen);
          }}
          open={isOpen}
          title={title}
          width={220}
        >
          {content()}
        </Popover>
      ) : (
        <React.Fragment>
          {content()}
          <Drawer
            css={drawerStyle}
            data-auto-testid="filter-info-icon-popover"
            data-testid="filter-info-icon-popover"
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            position="bottom"
            title={title}
            titleAlign="left"
          >
            <Drawer.Section>{popoverContent?.message}</Drawer.Section>
          </Drawer>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
